// app/career-map/[slug].tsx
// Career Path Detail Page — Full Production Version
// Features: 4 tabs (Overview/Skills/Roadmap/Jobs), skills checklist (AsyncStorage),
// roadmap progress tracking (AsyncStorage), salary table, companies, CTA

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Head from 'expo-router/head';
import { useLocalSearchParams, useRouter } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  ArrowLeft,
  BookmarkCheck,
  BookmarkPlus,
  Briefcase,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Circle,
  Map,
  MessageCircle,
  Share2,
  Sparkles,
  Square,
  TrendingUp,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { trackPageView, trackButtonClick, trackFunnelStep } from '@/utils/analytics';
import {
  CAREERS,
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  DEMAND_COLORS,
  DEMAND_LABELS,
  SKILL_LEVEL_COLORS,
  SKILL_LEVEL_LABELS,
  Career,
  CareerSkill,
  RoadmapStage,
  SalaryBand,
  findCareer,
  formatSalary,
  getRelatedCareers,
} from '@/utils/careerData';

// ─── Constants ──────────────────────────────────────────────────────────────

const BG           = '#F2EBDD';
const TEXT         = '#080C18';
const MUTED        = '#7A6F5F';
const CARD         = '#FAF5EA';
const BORDER       = 'rgba(15,23,42,0.08)';
const PRIMARY      = '#C8531E';
const PRIMARY_SOFT = 'rgba(200,83,30,0.18)';
const SUCCESS      = '#10B981';
const SUCCESS_SOFT = 'rgba(16,185,129,0.10)';

type Tab = 'overview' | 'skills' | 'roadmap' | 'jobs';

const TABS: Array<{ key: Tab; label: string; icon: string }> = [
  { key: 'overview', label: 'סקירה',     icon: '📋' },
  { key: 'skills',   label: 'כישורים',   icon: '💡' },
  { key: 'roadmap',  label: 'מפת למידה', icon: '🗺️' },
  { key: 'jobs',     label: 'שוק עבודה', icon: '💼' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function savedKey(slug: string)       { return `career_saved_v1`;          }
function skillsKey(slug: string)      { return `career_skills_${slug}`;    }
function roadmapKey(slug: string)     { return `career_roadmap_${slug}`;   }

// ─── Sub-components ─────────────────────────────────────────────────────────

// Quick stat chip used in hero
function StatChip({ icon, label, value, highlight }: {
  icon:       string;
  label:      string;
  value:      string;
  highlight?: boolean;
}) {
  return (
    <View style={[ds.statChip, highlight && ds.statChipHL]}>
      <Text style={ds.statChipIcon}>{icon}</Text>
      <View>
        <Text style={[ds.statChipVal, highlight && { color: PRIMARY }]}>{value}</Text>
        <Text style={ds.statChipLabel}>{label}</Text>
      </View>
    </View>
  );
}

// Tab button
function TabBtn({ tab, active, onPress }: { tab: typeof TABS[0]; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={[ds.tabBtn, active && ds.tabBtnActive]}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      accessibilityLabel={tab.label}
    >
      <Text style={ds.tabIcon}>{tab.icon}</Text>
      <Text style={[ds.tabLabel, active && ds.tabLabelActive]}>{tab.label}</Text>
    </Pressable>
  );
}

// Skill item with checkbox
function SkillItem({ skill, checked, onToggle }: {
  skill:    CareerSkill;
  checked:  boolean;
  onToggle: () => void;
}) {
  const levelColor = SKILL_LEVEL_COLORS[skill.level];
  return (
    <Pressable
      onPress={onToggle}
      style={[ds.skillItem, checked && ds.skillItemDone]}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={`${skill.name} — ${checked ? 'הושלם' : 'לא הושלם'}`}
    >
      <View style={ds.skillLeft}>
        {checked
          ? <CheckSquare size={18} color={SUCCESS} />
          : <Square      size={18} color={MUTED}   />
        }
      </View>
      <View style={ds.skillBody}>
        <View style={ds.skillNameRow}>
          <Text style={[ds.skillName, checked && ds.skillNameDone]}>{skill.name}</Text>
          {skill.required && (
            <View style={ds.requiredBadge}>
              <Text style={ds.requiredText}>נדרש</Text>
            </View>
          )}
        </View>
        <View style={[ds.levelBar, { backgroundColor: levelColor + '20' }]}>
          <Text style={[ds.levelText, { color: levelColor }]}>{SKILL_LEVEL_LABELS[skill.level]}</Text>
        </View>
      </View>
    </Pressable>
  );
}

// Roadmap stage card
function StageCard({ stage, completed, expanded, onToggleComplete, onToggleExpand }: {
  stage:            RoadmapStage;
  completed:        boolean;
  expanded:         boolean;
  onToggleComplete: () => void;
  onToggleExpand:   () => void;
}) {
  return (
    <View style={[ds.stageCard, completed && ds.stageCardDone]}>
      {/* Stage header */}
      <Pressable
        onPress={onToggleExpand}
        style={ds.stageHeader}
        accessibilityRole="button"
        accessibilityLabel={`שלב ${stage.number}: ${stage.title}`}
      >
        {/* Number circle */}
        <View style={[ds.stageNum, completed && ds.stageNumDone]}>
          {completed
            ? <CheckCircle size={16} color="#FAF5EA" />
            : <Text style={ds.stageNumText}>{stage.number}</Text>
          }
        </View>

        {/* Info */}
        <View style={ds.stageInfo}>
          <Text style={[ds.stageTitle, completed && ds.stageTitleDone]}>{stage.title}</Text>
          <Text style={ds.stageDuration}>{stage.duration}</Text>
        </View>

        {/* Expand toggle */}
        <Pressable onPress={onToggleExpand} style={ds.stageExpBtn} accessibilityRole="button">
          {expanded ? <ChevronUp size={16} color={MUTED} /> : <ChevronDown size={16} color={MUTED} />}
        </Pressable>
      </Pressable>

      {/* Expandable body */}
      {expanded && (
        <View style={ds.stageBody}>
          <Text style={ds.stageDesc}>{stage.description}</Text>

          {/* Topics */}
          <Text style={ds.stageSubtitle}>נושאים</Text>
          <View style={ds.topicsGrid}>
            {stage.topics.map((t, i) => (
              <View key={i} style={ds.topicChip}>
                <Text style={ds.topicText}>{t}</Text>
              </View>
            ))}
          </View>

          {/* Outcomes */}
          <Text style={ds.stageSubtitle}>מה תדע לעשות</Text>
          {stage.outcomes.map((o, i) => (
            <View key={i} style={ds.outcomeRow}>
              <Text style={ds.outcomeDot}>✓</Text>
              <Text style={ds.outcomeText}>{o}</Text>
            </View>
          ))}

          {/* Complete toggle */}
          <Pressable
            onPress={onToggleComplete}
            style={[ds.stageDoneBtn, completed && ds.stageDoneBtnActive]}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: completed }}
            accessibilityLabel={completed ? 'סמן כלא הושלם' : 'סמן כהושלם'}
          >
            {completed
              ? <CheckCircle size={15} color={SUCCESS} />
              : <Circle      size={15} color={MUTED}   />
            }
            <Text style={[ds.stageDoneBtnText, completed && { color: SUCCESS }]}>
              {completed ? 'הושלם ✓' : 'סמן כהושלם'}
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
}

// Salary band row
function SalaryRow({ band, isLast }: { band: SalaryBand; isLast: boolean }) {
  const fillPct = Math.min(100, Math.round((band.max / 60000) * 100));
  return (
    <View style={[ds.salaryRow, !isLast && ds.salaryRowDivider]}>
      <View style={ds.salaryLeft}>
        <Text style={ds.salaryLabel}>{band.label}</Text>
        <Text style={ds.salaryExp}>{band.experience}</Text>
      </View>
      <View style={ds.salaryRight}>
        <Text style={ds.salaryRange}>
          {formatSalary(band.min)} – {formatSalary(band.max)}
        </Text>
        <View style={ds.salaryBar}>
          <View style={[ds.salaryBarFill, { width: `${fillPct}%` }]} />
        </View>
      </View>
    </View>
  );
}

// Related career chip
function RelatedChip({ career, onPress }: { career: Career; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [ds.relatedChip, pressed && { opacity: 0.85 }]}
      accessibilityRole="button"
      accessibilityLabel={`עבור למסלול ${career.title}`}
    >
      <Text style={ds.relatedEmoji}>{career.emoji}</Text>
      <Text style={ds.relatedChipTitle}>{career.shortTitle}</Text>
      <ChevronLeft size={12} color={MUTED} />
    </Pressable>
  );
}

function ChevronLeft({ size, color }: { size: number; color: string }) {
  return <ChevronRight size={size} color={color} style={{ transform: [{ scaleX: -1 }] }} />;
}

// Progress bar component
function ProgressBar({ value, total, color }: { value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <View style={ds.progressWrap}>
      <View style={ds.progressTrack}>
        <View style={[ds.progressFill, { width: `${pct}%`, backgroundColor: color }]} />
      </View>
      <Text style={ds.progressLabel}>{pct}%</Text>
    </View>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function CareerDetailPage() {
  const router = useRouter();
  const { slug } = useLocalSearchParams<{ slug: string }>();

  const career = useMemo(() => findCareer(slug ?? ''), [slug]);
  const related = useMemo(() => (career ? getRelatedCareers(career) : []), [career]);

  const [activeTab,       setActiveTab]       = useState<Tab>('overview');
  const [checkedSkills,   setCheckedSkills]   = useState<string[]>([]);
  const [completedStages, setCompletedStages] = useState<string[]>([]);
  const [expandedStages,  setExpandedStages]  = useState<string[]>([]);
  const [saved,           setSaved]           = useState(false);
  const [loading,         setLoading]         = useState(true);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Load persisted state ──
  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const [rawSkills, rawStages, rawSaved] = await Promise.all([
          AsyncStorage.getItem(skillsKey(slug)),
          AsyncStorage.getItem(roadmapKey(slug)),
          AsyncStorage.getItem('career_saved_v1'),
        ]);
        if (!mountedRef.current) return;
        setCheckedSkills(rawSkills  ? JSON.parse(rawSkills)  : []);
        setCompletedStages(rawStages ? JSON.parse(rawStages) : []);
        const savedArr: string[] = rawSaved ? JSON.parse(rawSaved) : [];
        setSaved(savedArr.includes(slug));
      } catch {
        // quiet
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();
  }, [slug]);

  // ── Analytics ──
  useEffect(() => {
    if (!slug) return;
    void trackPageView(`/career-map/${slug}`);
    void trackFunnelStep('course_view', { career_slug: slug });
  }, [slug]);

  // ── Save toggle ──
  const toggleSaved = useCallback(async () => {
    if (!slug) return;
    try {
      const raw = await AsyncStorage.getItem('career_saved_v1');
      const arr: string[] = raw ? JSON.parse(raw) : [];
      const next = arr.includes(slug) ? arr.filter((s) => s !== slug) : [...arr, slug];
      await AsyncStorage.setItem('career_saved_v1', JSON.stringify(next));
      if (mountedRef.current) setSaved(next.includes(slug));
      void trackButtonClick('career_save_detail', `/career-map/${slug}`);
    } catch {}
  }, [slug]);

  // ── Skill toggle ──
  const toggleSkill = useCallback((name: string) => {
    if (!slug) return;
    setCheckedSkills((prev) => {
      const next = prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name];
      void AsyncStorage.setItem(skillsKey(slug), JSON.stringify(next));
      return next;
    });
  }, [slug]);

  // ── Stage complete toggle ──
  const toggleStage = useCallback((id: string) => {
    if (!slug) return;
    setCompletedStages((prev) => {
      const next = prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id];
      void AsyncStorage.setItem(roadmapKey(slug), JSON.stringify(next));
      return next;
    });
  }, [slug]);

  // ── Stage expand toggle ──
  const toggleExpand = useCallback((id: string) => {
    setExpandedStages((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  }, []);

  // ── Navigate to related career ──
  const goRelated = useCallback((relSlug: string) => {
    void trackButtonClick('career_related', `/career-map/${slug}`, { target: relSlug });
    router.replace({ pathname: '/career-map/[slug]', params: { slug: relSlug } } as any);
  }, [router, slug]);

  // ─────────────────────────────────────────────────────────────────────────
  // Guard — career not found
  // ─────────────────────────────────────────────────────────────────────────

  if (!career) {
    return (
      <View style={ds.notFound}>
        <Text style={ds.notFoundEmoji}>🔍</Text>
        <Text style={ds.notFoundTitle}>מסלול לא נמצא</Text>
        <Pressable onPress={() => router.back()} style={ds.notFoundBtn} accessibilityRole="button">
          <Text style={ds.notFoundBtnText}>חזרה למסלולים</Text>
        </Pressable>
      </View>
    );
  }

  // ── Computed progress ──
  const totalSkills   = career.skills.length;
  const doneSkills    = checkedSkills.filter((n) => career.skills.some((s) => s.name === n)).length;
  const totalStages   = career.roadmap.length;
  const doneStages    = completedStages.filter((id) => career.roadmap.some((s) => s.id === id)).length;
  const catColor      = CATEGORY_COLORS[career.category];
  const demandColors  = DEMAND_COLORS[career.demand];

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>{career.title} — מסלול קריירה | Kursi</title>
          <meta name="description" content={career.description} />
          <link rel="canonical" href={`https://kursiaii.com/career-map/${career.slug}`} />
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        </Head>
      )}

      <ScrollView style={ds.scroll} contentContainerStyle={ds.content}>

        {/* ── Top bar ── */}
        <View style={ds.topBar}>
          <Pressable
            onPress={() => router.back()}
            style={ds.backBtn}
            accessibilityRole="button"
            accessibilityLabel="חזרה למסלולים"
          >
            <ArrowLeft size={16} color={TEXT} />
            <Text style={ds.backText}>מסלולים</Text>
          </Pressable>

          <View style={ds.topActions}>
            {/* Save */}
            <Pressable
              onPress={toggleSaved}
              style={[ds.topIconBtn, saved && ds.topIconBtnActive]}
              accessibilityRole="button"
              accessibilityLabel={saved ? 'הסר מהשמורים' : 'שמור מסלול'}
            >
              {saved
                ? <BookmarkCheck size={18} color={PRIMARY} />
                : <BookmarkPlus  size={18} color={MUTED}   />
              }
            </Pressable>

            {/* Share */}
            {Platform.OS === 'web' && (
              <Pressable
                onPress={() => {
                  void navigator?.clipboard?.writeText(`https://kursiaii.com/career-map/${career.slug}`);
                  void trackButtonClick('career_share', `/career-map/${slug}`);
                }}
                style={ds.topIconBtn}
                accessibilityRole="button"
                accessibilityLabel="העתק קישור"
              >
                <Share2 size={18} color={MUTED} />
              </Pressable>
            )}
          </View>
        </View>

        {/* ── Hero ── */}
        <View style={ds.hero}>
          {/* Emoji + category strip */}
          <View style={[ds.heroStrip, { backgroundColor: catColor + '12' }]}>
            <Text style={ds.heroEmoji}>{career.emoji}</Text>
            <View style={ds.heroBadges}>
              <View style={[ds.catBadge, { backgroundColor: catColor + '18', borderColor: catColor + '30' }]}>
                <Text style={[ds.catBadgeText, { color: catColor }]}>
                  {CATEGORY_LABELS[career.category]}
                </Text>
              </View>
              <View style={[ds.demandBadge, { backgroundColor: demandColors.bg, borderColor: demandColors.border }]}>
                <Text style={[ds.demandBadgeText, { color: demandColors.text }]}>
                  {DEMAND_LABELS[career.demand]}
                </Text>
              </View>
            </View>
          </View>

          <View style={ds.heroBody}>
            <Text style={ds.heroTitle}>{career.title}</Text>
            <Text style={ds.heroDesc}>{career.description}</Text>

            {/* Quick stats grid */}
            <View style={ds.statsGrid}>
              <StatChip icon="💰" label="Junior" value={`${formatSalary(career.salaryMin)}/חודש`} />
              <StatChip icon="🚀" label="Senior" value={`${formatSalary(career.salaryMax)}/חודש`} highlight />
              <StatChip icon="⏱️" label="זמן לימוד" value={career.timeToLearn} />
              <StatChip icon="📈" label="צמיחה/שנה" value={`+${career.jobGrowthRate}%`} highlight />
              <StatChip icon="💼" label="משרות/חודש" value={`~${career.avgMonthlyJobs}`} />
              <StatChip icon="🎯" label="כישורים" value={`${totalSkills} כישורים`} />
            </View>

            {/* Progress row */}
            {(doneSkills > 0 || doneStages > 0) && (
              <View style={ds.progressSection}>
                <Text style={ds.progressTitle}>ההתקדמות שלך</Text>
                <View style={ds.progressRows}>
                  <View style={ds.progressItem}>
                    <Text style={ds.progressItemLabel}>כישורים ({doneSkills}/{totalSkills})</Text>
                    <ProgressBar value={doneSkills} total={totalSkills} color={PRIMARY} />
                  </View>
                  <View style={ds.progressItem}>
                    <Text style={ds.progressItemLabel}>שלבי למידה ({doneStages}/{totalStages})</Text>
                    <ProgressBar value={doneStages} total={totalStages} color={SUCCESS} />
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* ── Tab Nav ── */}
        <View style={ds.tabNav}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={ds.tabNavInner}>
            {TABS.map((tab) => (
              <TabBtn
                key={tab.key}
                tab={tab}
                active={activeTab === tab.key}
                onPress={() => setActiveTab(tab.key)}
              />
            ))}
          </ScrollView>
        </View>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB: OVERVIEW                                                      */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <View style={ds.tabContent}>
            {/* Long description */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>על המסלול</Text>
              <Text style={ds.sectionText}>{career.longDescription}</Text>
            </View>

            {/* Pros */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>יתרונות 👍</Text>
              <View style={ds.proConList}>
                {career.pros.map((pro, i) => (
                  <View key={i} style={ds.proItem}>
                    <Text style={ds.proDot}>✓</Text>
                    <Text style={ds.proText}>{pro}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Cons */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>אתגרים 🤔</Text>
              <View style={ds.proConList}>
                {career.cons.map((con, i) => (
                  <View key={i} style={ds.conItem}>
                    <Text style={ds.conDot}>!</Text>
                    <Text style={ds.conText}>{con}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Best for */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>מתאים למי ש...</Text>
              <View style={ds.bestForList}>
                {career.bestFor.map((b, i) => (
                  <View key={i} style={ds.bestForChip}>
                    <Text style={ds.bestForText}>{b}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Top Companies */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>חברות מובילות</Text>
              <View style={ds.companiesGrid}>
                {career.topCompanies.map((company, i) => (
                  <View key={i} style={ds.companyChip}>
                    <Text style={ds.companyText}>{company}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB: SKILLS                                                        */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'skills' && (
          <View style={ds.tabContent}>
            {/* Progress summary */}
            <View style={ds.skillsHeader}>
              <View>
                <Text style={ds.sectionTitle}>רשימת כישורים</Text>
                <Text style={ds.skillsSubtitle}>סמן את הכישורים שכבר יש לך</Text>
              </View>
              <View style={ds.skillsProgress}>
                <Text style={ds.skillsProgressNum}>{doneSkills}/{totalSkills}</Text>
                <Text style={ds.skillsProgressLabel}>הושלמו</Text>
              </View>
            </View>

            <ProgressBar value={doneSkills} total={totalSkills} color={PRIMARY} />

            {/* Required skills */}
            <Text style={ds.skillsGroupTitle}>🔴 כישורים נדרשים</Text>
            {career.skills.filter((s) => s.required).map((skill) => (
              <SkillItem
                key={skill.name}
                skill={skill}
                checked={checkedSkills.includes(skill.name)}
                onToggle={() => toggleSkill(skill.name)}
              />
            ))}

            {/* Optional skills */}
            {career.skills.some((s) => !s.required) && (
              <>
                <Text style={[ds.skillsGroupTitle, { marginTop: 16 }]}>🟡 כישורים מועילים (לא חובה)</Text>
                {career.skills.filter((s) => !s.required).map((skill) => (
                  <SkillItem
                    key={skill.name}
                    skill={skill}
                    checked={checkedSkills.includes(skill.name)}
                    onToggle={() => toggleSkill(skill.name)}
                  />
                ))}
              </>
            )}

            {/* Skill levels legend */}
            <View style={ds.legendBox}>
              <Text style={ds.legendTitle}>רמות כישור</Text>
              <View style={ds.legendRow}>
                {(['beginner', 'intermediate', 'advanced'] as const).map((lvl) => (
                  <View key={lvl} style={ds.legendItem}>
                    <View style={[ds.legendDot, { backgroundColor: SKILL_LEVEL_COLORS[lvl] }]} />
                    <Text style={ds.legendText}>{SKILL_LEVEL_LABELS[lvl]}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB: ROADMAP                                                       */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'roadmap' && (
          <View style={ds.tabContent}>
            {/* Progress summary */}
            <View style={ds.skillsHeader}>
              <View>
                <Text style={ds.sectionTitle}>מפת למידה</Text>
                <Text style={ds.skillsSubtitle}>שלב אחר שלב — מהתחלה לפרודקשן</Text>
              </View>
              <View style={ds.skillsProgress}>
                <Text style={ds.skillsProgressNum}>{doneStages}/{totalStages}</Text>
                <Text style={ds.skillsProgressLabel}>שלבים</Text>
              </View>
            </View>

            <ProgressBar value={doneStages} total={totalStages} color={SUCCESS} />

            {/* Estimated total time */}
            <View style={ds.timeEstimate}>
              <Map size={16} color={PRIMARY} />
              <Text style={ds.timeEstimateText}>
                זמן כולל משוער: {career.timeToLearn}
              </Text>
            </View>

            {/* Roadmap stages */}
            <View style={ds.stagesList}>
              {career.roadmap.map((stage, idx) => (
                <View key={stage.id} style={ds.stageWrapper}>
                  {/* Connector line (not for last) */}
                  {idx < career.roadmap.length - 1 && (
                    <View style={[
                      ds.stageLine,
                      completedStages.includes(stage.id) && ds.stageLineDone,
                    ]} />
                  )}
                  <StageCard
                    stage={stage}
                    completed={completedStages.includes(stage.id)}
                    expanded={expandedStages.includes(stage.id)}
                    onToggleComplete={() => toggleStage(stage.id)}
                    onToggleExpand={() => toggleExpand(stage.id)}
                  />
                </View>
              ))}
            </View>

            {/* Completion message */}
            {doneStages === totalStages && totalStages > 0 && (
              <View style={ds.completionBanner}>
                <Text style={ds.completionEmoji}>🎉</Text>
                <Text style={ds.completionTitle}>השלמת את כל השלבים!</Text>
                <Text style={ds.completionSub}>מוכן לשוק העבודה. הגיע הזמן לעדכן את הפרופיל!</Text>
              </View>
            )}
          </View>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* TAB: JOBS                                                          */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'jobs' && (
          <View style={ds.tabContent}>
            {/* Market overview */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>נתוני שוק העבודה</Text>
              <View style={ds.marketCards}>
                <View style={ds.marketCard}>
                  <Text style={ds.marketCardNum}>{career.avgMonthlyJobs}</Text>
                  <Text style={ds.marketCardLabel}>משרות פתוחות בממוצע לחודש</Text>
                </View>
                <View style={[ds.marketCard, ds.marketCardHL]}>
                  <Text style={[ds.marketCardNum, { color: PRIMARY }]}>+{career.jobGrowthRate}%</Text>
                  <Text style={ds.marketCardLabel}>צמיחה שנתית בביקוש</Text>
                </View>
                <View style={ds.marketCard}>
                  <Text style={ds.marketCardNum}>{DEMAND_LABELS[career.demand]}</Text>
                  <Text style={ds.marketCardLabel}>רמת ביקוש נוכחית</Text>
                </View>
              </View>
            </View>

            {/* Salary table */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>טבלת שכר לפי ניסיון</Text>
              <Text style={ds.sectionSubtitle}>* שכר ברוטו לחודש בשוק הישראלי (ILS)</Text>
              <View style={ds.salaryTable}>
                <View style={ds.salaryTableHeader}>
                  <Text style={[ds.salaryHeaderCell, { flex: 1 }]}>רמה</Text>
                  <Text style={[ds.salaryHeaderCell, { flex: 1 }]}>ניסיון</Text>
                  <Text style={[ds.salaryHeaderCell, { flex: 1.4 }]}>טווח שכר</Text>
                </View>
                {career.salaryBands.map((band, i) => (
                  <SalaryRow
                    key={band.label}
                    band={band}
                    isLast={i === career.salaryBands.length - 1}
                  />
                ))}
              </View>
            </View>

            {/* Salary range visual */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>טווח שכר כולל</Text>
              <View style={ds.salaryVisual}>
                <View style={ds.salaryVisualBar}>
                  <View style={ds.salaryVisualFill} />
                  <View style={[ds.salaryVisualDot, { left: '0%' }]}>
                    <Text style={ds.salaryVisualLabel}>{formatSalary(career.salaryMin)}</Text>
                  </View>
                  <View style={[ds.salaryVisualDot, { left: '100%' }]}>
                    <Text style={[ds.salaryVisualLabel, { right: 0, left: 'auto' }]}>{formatSalary(career.salaryMax)}</Text>
                  </View>
                </View>
                <View style={ds.salaryVisualLegend}>
                  <Text style={ds.salaryVisualMin}>Junior: {formatSalary(career.salaryMin)}</Text>
                  <Text style={ds.salaryVisualMax}>Senior: {formatSalary(career.salaryMax)}</Text>
                </View>
              </View>
            </View>

            {/* Top companies */}
            <View style={ds.section}>
              <Text style={ds.sectionTitle}>חברות מובילות שמגייסות</Text>
              <View style={ds.companiesGrid}>
                {career.topCompanies.map((company, i) => (
                  <View key={i} style={[ds.companyChip, ds.companyChipJobs]}>
                    <Briefcase size={11} color={PRIMARY} />
                    <Text style={ds.companyText}>{company}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Job search tips */}
            <View style={ds.tipsBox}>
              <Text style={ds.tipsTitle}>💡 טיפים לחיפוש עבודה</Text>
              <View style={ds.tipItem}>
                <Text style={ds.tipDot}>1.</Text>
                <Text style={ds.tipText}>בנה פורטפוליו עם 2-3 פרויקטים אמיתיים ב-GitHub</Text>
              </View>
              <View style={ds.tipItem}>
                <Text style={ds.tipDot}>2.</Text>
                <Text style={ds.tipText}>עדכן LinkedIn עם כישורים רלוונטיים ותיאורים מפורטים</Text>
              </View>
              <View style={ds.tipItem}>
                <Text style={ds.tipDot}>3.</Text>
                <Text style={ds.tipText}>הגש לפחות 10 מועמדויות בשבוע — גיוס הוא משחק מספרים</Text>
              </View>
              <View style={ds.tipItem}>
                <Text style={ds.tipDot}>4.</Text>
                <Text style={ds.tipText}>תרגל LeetCode/HackerRank לפני ראיונות טכניים</Text>
              </View>
            </View>
          </View>
        )}

        {/* ── CTA Section ── */}
        <View style={ds.ctaSection}>
          <Text style={ds.ctaTitle}>מוכן להתחיל ב{career.shortTitle}?</Text>
          <Text style={ds.ctaSub}>מצא קורסים מותאמים אישית לקריירה הזו</Text>
          <View style={ds.ctaBtns}>
            <Pressable
              onPress={() => {
                void trackButtonClick('cta_catalog', `/career-map/${slug}`, { topic: career.catalogTopic });
                router.push({
                  pathname: '/catalog' as any,
                  params: { topic: encodeURIComponent(career.catalogTopic) } as any,
                });
              }}
              style={({ pressed }) => [ds.ctaBtn, pressed && { opacity: 0.88 }]}
              accessibilityRole="button"
              accessibilityLabel="ראה קורסים רלוונטיים"
            >
              <TrendingUp size={16} color="#FAF5EA" />
              <Text style={ds.ctaBtnText}>קורסים לקריירה זו</Text>
            </Pressable>

            <Pressable
              onPress={() => {
                void trackButtonClick('cta_chat_career', `/career-map/${slug}`);
                router.push({
                  pathname: '/chat' as any,
                  params: { seed: career.chatSeed } as any,
                });
              }}
              style={({ pressed }) => [ds.ctaBtnSecondary, pressed && { opacity: 0.88 }]}
              accessibilityRole="button"
              accessibilityLabel="שוחח עם AI על המסלול"
            >
              <MessageCircle size={16} color={PRIMARY} />
              <Text style={ds.ctaBtnSecText}>שאל את ה-AI</Text>
            </Pressable>
          </View>
        </View>

        {/* ── Related Careers ── */}
        {related.length > 0 && (
          <View style={ds.relatedSection}>
            <Text style={ds.relatedTitle}>מסלולים קשורים</Text>
            <View style={ds.relatedRow}>
              {related.map((rel) => (
                <RelatedChip key={rel.slug} career={rel} onPress={() => goRelated(rel.slug)} />
              ))}
            </View>
          </View>
        )}

      </ScrollView>
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const ds = StyleSheet.create({
  scroll:  { flex: 1, backgroundColor: BG },
  content: {
    paddingTop:        Platform.OS === 'web' ? 22 : 16,
    paddingHorizontal: Platform.OS === 'web' ? 22 : 16,
    paddingBottom:     Platform.OS === 'web' ? 56 : 36,
    alignItems:        'center',
  },

  // Not found
  notFound: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 } as any,
  notFoundEmoji: { fontSize: 48 },
  notFoundTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT },
  notFoundBtn: {
    marginTop:       8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius:    12,
    backgroundColor: PRIMARY,
  },
  notFoundBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: '#FAF5EA' },

  // Top bar
  topBar: {
    width:         '100%',
    maxWidth:      Platform.OS === 'web' ? 780 : 720,
    flexDirection: 'row-reverse',
    alignItems:    'center',
    justifyContent:'space-between',
    marginBottom:  14,
  },
  backBtn: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius:    999,
    backgroundColor: CARD,
    borderWidth:     1,
    borderColor:     BORDER,
  } as any,
  backText: { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT },
  topActions: { flexDirection: 'row-reverse', gap: 8 } as any,
  topIconBtn: {
    width:           38,
    height:          38,
    borderRadius:    10,
    backgroundColor: CARD,
    borderWidth:     1,
    borderColor:     BORDER,
    alignItems:      'center',
    justifyContent:  'center',
  },
  topIconBtnActive: { backgroundColor: PRIMARY_SOFT, borderColor: 'rgba(200,83,30,0.18)' },

  // Hero
  hero: {
    width:        '100%',
    maxWidth:     Platform.OS === 'web' ? 780 : 720,
    backgroundColor: CARD,
    borderRadius: 0,
    borderWidth:  1,
    borderColor:  BORDER,
    overflow:     'hidden',
    marginBottom: 14,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 24px rgba(15,23,42,0.08)' } as any : {}),
  } as any,
  heroStrip: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    justifyContent:  'space-between',
    paddingHorizontal: 16,
    paddingVertical:  12,
  } as any,
  heroEmoji:  { fontSize: 44 },
  heroBadges: { gap: 6 } as any,
  catBadge: {
    paddingVertical:   3,
    paddingHorizontal: 10,
    borderRadius:      999,
    borderWidth:       1,
    alignSelf:         'flex-end',
  },
  catBadgeText:    { fontFamily: theme.fonts.bold, fontSize: 11.5 },
  demandBadge: {
    paddingVertical:   3,
    paddingHorizontal: 10,
    borderRadius:      999,
    borderWidth:       1,
    alignSelf:         'flex-end',
  },
  demandBadgeText: { fontFamily: theme.fonts.bold, fontSize: 11.5 },

  heroBody: { padding: 16 },
  heroTitle: { fontFamily: theme.fonts.bold, fontSize: Platform.OS === 'web' ? 28 : 22, color: TEXT, textAlign: 'right' },
  heroDesc:  {
    marginTop:  6,
    fontFamily: theme.fonts.regular,
    fontSize:   14,
    color:      MUTED,
    textAlign:  'right',
    lineHeight: 22,
  },

  statsGrid: {
    marginTop:     12,
    flexDirection: 'row-reverse',
    flexWrap:      'wrap',
    gap:           8,
  } as any,
  statChip: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius:    12,
    backgroundColor: 'rgba(15,23,42,0.04)',
    borderWidth:     1,
    borderColor:     BORDER,
    minWidth:        120,
  } as any,
  statChipHL: { backgroundColor: PRIMARY_SOFT, borderColor: 'rgba(200,83,30,0.18)' },
  statChipIcon:  { fontSize: 18 },
  statChipVal:   { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT },
  statChipLabel: { fontFamily: theme.fonts.regular, fontSize: 10.5, color: MUTED },

  // Progress
  progressSection: {
    marginTop:       14,
    padding:         12,
    backgroundColor: 'rgba(15,23,42,0.03)',
    borderRadius:    12,
    borderWidth:     1,
    borderColor:     BORDER,
  },
  progressTitle:       { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT, textAlign: 'right', marginBottom: 8 },
  progressRows:        { gap: 8 } as any,
  progressItem:        { gap: 4 } as any,
  progressItemLabel:   { fontFamily: theme.fonts.medium, fontSize: 12, color: MUTED, textAlign: 'right' },
  progressWrap:        { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 } as any,
  progressTrack:       { flex: 1, height: 6, backgroundColor: 'rgba(15,23,42,0.08)', borderRadius: 999 },
  progressFill:        { height: '100%', borderRadius: 999 },
  progressLabel:       { fontFamily: theme.fonts.bold, fontSize: 11, color: MUTED, minWidth: 28, textAlign: 'right' },

  // Tab nav
  tabNav: {
    width:        '100%',
    maxWidth:     Platform.OS === 'web' ? 780 : 720,
    marginBottom: 14,
    backgroundColor: CARD,
    borderRadius: 0,
    borderWidth:  1,
    borderColor:  BORDER,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 12px rgba(15,23,42,0.06)' } as any : {}),
  } as any,
  tabNavInner: {
    flexDirection: 'row-reverse',
    padding:       6,
    gap:           4,
  } as any,
  tabBtn: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             5,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius:    10,
    flex:            1,
    justifyContent:  'center',
  } as any,
  tabBtnActive:    { backgroundColor: PRIMARY },
  tabIcon:         { fontSize: 14 },
  tabLabel:        { fontFamily: theme.fonts.medium, fontSize: 13, color: MUTED },
  tabLabelActive:  { color: '#FAF5EA', fontFamily: theme.fonts.bold },

  // Tab content
  tabContent: {
    width:    '100%',
    maxWidth: Platform.OS === 'web' ? 780 : 720,
    gap:      14,
  } as any,

  // Sections
  section: {
    backgroundColor: CARD,
    borderRadius:    16,
    borderWidth:     1,
    borderColor:     BORDER,
    padding:         16,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 12px rgba(15,23,42,0.05)' } as any : {}),
  } as any,
  sectionTitle:    { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right', marginBottom: 10 },
  sectionSubtitle: { fontFamily: theme.fonts.regular, fontSize: 12, color: MUTED, textAlign: 'right', marginBottom: 8 },
  sectionText:     { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT, textAlign: 'right', lineHeight: 24 },

  // Pros / Cons
  proConList: { gap: 8 } as any,
  proItem: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8 } as any,
  proDot:  { fontFamily: theme.fonts.bold, fontSize: 14, color: SUCCESS, width: 16 },
  proText: { flex: 1, fontFamily: theme.fonts.regular, fontSize: 13.5, color: TEXT, textAlign: 'right', lineHeight: 22 },
  conItem: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8 } as any,
  conDot:  { fontFamily: theme.fonts.bold, fontSize: 14, color: '#F59E0B', width: 16 },
  conText: { flex: 1, fontFamily: theme.fonts.regular, fontSize: 13.5, color: TEXT, textAlign: 'right', lineHeight: 22 },

  // Best for
  bestForList: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8 } as any,
  bestForChip: {
    paddingVertical:   6,
    paddingHorizontal: 12,
    borderRadius:      999,
    backgroundColor:   PRIMARY_SOFT,
    borderWidth:       1,
    borderColor:       'rgba(200,83,30,0.18)',
  },
  bestForText: { fontFamily: theme.fonts.medium, fontSize: 12.5, color: PRIMARY },

  // Companies
  companiesGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8, marginTop: 4 } as any,
  companyChip: {
    paddingVertical:   6,
    paddingHorizontal: 12,
    borderRadius:      8,
    backgroundColor:   'rgba(15,23,42,0.04)',
    borderWidth:       1,
    borderColor:       BORDER,
  },
  companyChipJobs: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           5,
  } as any,
  companyText: { fontFamily: theme.fonts.medium, fontSize: 12.5, color: TEXT },

  // Skills tab
  skillsHeader: {
    flexDirection:  'row-reverse',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   8,
  },
  skillsSubtitle:     { fontFamily: theme.fonts.regular, fontSize: 12.5, color: MUTED, textAlign: 'right', marginTop: 2 },
  skillsProgress:     { alignItems: 'center', backgroundColor: PRIMARY_SOFT, padding: 10, borderRadius: 12, minWidth: 60 },
  skillsProgressNum:  { fontFamily: theme.fonts.bold, fontSize: 18, color: PRIMARY },
  skillsProgressLabel:{ fontFamily: theme.fonts.regular, fontSize: 10, color: MUTED },
  skillsGroupTitle:   { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT, textAlign: 'right', marginTop: 12, marginBottom: 6 },

  skillItem: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: CARD,
    borderRadius:    12,
    borderWidth:     1,
    borderColor:     BORDER,
    marginBottom:    6,
  } as any,
  skillItemDone: { backgroundColor: SUCCESS_SOFT, borderColor: 'rgba(16,185,129,0.22)' },
  skillLeft:     { width: 24, alignItems: 'center' },
  skillBody:     { flex: 1, gap: 4 } as any,
  skillNameRow:  { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 } as any,
  skillName:     { fontFamily: theme.fonts.medium, fontSize: 14, color: TEXT, textAlign: 'right' },
  skillNameDone: { color: SUCCESS, textDecorationLine: 'line-through' },
  requiredBadge: { paddingVertical: 1, paddingHorizontal: 6, borderRadius: 4, backgroundColor: 'rgba(239,68,68,0.10)' },
  requiredText:  { fontFamily: theme.fonts.bold, fontSize: 10, color: '#DC2626' },
  levelBar: {
    alignSelf:         'flex-end',
    paddingVertical:   2,
    paddingHorizontal: 8,
    borderRadius:      4,
  },
  levelText:  { fontFamily: theme.fonts.medium, fontSize: 10.5 },

  // Legend
  legendBox: {
    marginTop:       12,
    padding:         12,
    backgroundColor: 'rgba(15,23,42,0.03)',
    borderRadius:    10,
    borderWidth:     1,
    borderColor:     BORDER,
  },
  legendTitle: { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT, textAlign: 'right', marginBottom: 8 },
  legendRow:   { flexDirection: 'row-reverse', gap: 16 } as any,
  legendItem:  { flexDirection: 'row-reverse', alignItems: 'center', gap: 6 } as any,
  legendDot:   { width: 8, height: 8, borderRadius: 9999 },
  legendText:  { fontFamily: theme.fonts.regular, fontSize: 12, color: MUTED },

  // Roadmap tab
  timeEstimate: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: PRIMARY_SOFT,
    borderRadius:    10,
    borderWidth:     1,
    borderColor:     'rgba(200,83,30,0.18)',
    alignSelf:       'flex-start',
    marginBottom:    4,
  } as any,
  timeEstimateText: { fontFamily: theme.fonts.medium, fontSize: 13, color: PRIMARY },

  stagesList:   { gap: 12 } as any,
  stageWrapper: { position: 'relative' } as any,
  stageLine: {
    position:        'absolute',
    left:            Platform.OS === 'web' ? 20 : 19,
    top:             42,
    bottom:          -12,
    width:           2,
    backgroundColor: BORDER,
    zIndex:          0,
  } as any,
  stageLineDone: { backgroundColor: SUCCESS },

  stageCard: {
    backgroundColor: CARD,
    borderRadius:    14,
    borderWidth:     1,
    borderColor:     BORDER,
    overflow:        'hidden',
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 10px rgba(15,23,42,0.06)' } as any : {}),
  } as any,
  stageCardDone: { borderColor: 'rgba(16,185,129,0.30)', backgroundColor: SUCCESS_SOFT },

  stageHeader: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             12,
    padding:         14,
  } as any,
  stageNum: {
    width:           36,
    height:          36,
    borderRadius:    18,
    backgroundColor: 'rgba(200,83,30,0.18)',
    borderWidth:     2,
    borderColor:     PRIMARY,
    alignItems:      'center',
    justifyContent:  'center',
    flexShrink:      0,
  },
  stageNumDone:    { backgroundColor: SUCCESS, borderColor: SUCCESS },
  stageNumText:    { fontFamily: theme.fonts.bold, fontSize: 14, color: PRIMARY },
  stageInfo:       { flex: 1, gap: 2 } as any,
  stageTitle:      { fontFamily: theme.fonts.bold, fontSize: 14.5, color: TEXT, textAlign: 'right' },
  stageTitleDone:  { color: SUCCESS },
  stageDuration:   { fontFamily: theme.fonts.regular, fontSize: 12, color: MUTED, textAlign: 'right' },
  stageExpBtn:     { padding: 4 },

  stageBody: {
    paddingHorizontal: 14,
    paddingBottom:     14,
    borderTopWidth:    1,
    borderTopColor:    BORDER,
    paddingTop:        12,
  },
  stageDesc:     { fontFamily: theme.fonts.regular, fontSize: 13.5, color: MUTED, textAlign: 'right', lineHeight: 21, marginBottom: 10 },
  stageSubtitle: { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT, textAlign: 'right', marginBottom: 6, marginTop: 8 },

  topicsGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 6, marginBottom: 4 } as any,
  topicChip: {
    paddingVertical:   4,
    paddingHorizontal: 10,
    borderRadius:      6,
    backgroundColor:   'rgba(200,83,30,0.18)',
    borderWidth:       1,
    borderColor:       'rgba(200,83,30,0.18)',
  },
  topicText: { fontFamily: theme.fonts.medium, fontSize: 11.5, color: PRIMARY },

  outcomeRow: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 6, marginBottom: 4 } as any,
  outcomeDot: { fontFamily: theme.fonts.bold, fontSize: 13, color: SUCCESS },
  outcomeText:{ flex: 1, fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT, textAlign: 'right' },

  stageDoneBtn: {
    marginTop:       12,
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius:    10,
    borderWidth:     1,
    borderColor:     BORDER,
    backgroundColor: 'rgba(15,23,42,0.03)',
    alignSelf:       'flex-end',
  } as any,
  stageDoneBtnActive: { borderColor: 'rgba(16,185,129,0.30)', backgroundColor: SUCCESS_SOFT },
  stageDoneBtnText:   { fontFamily: theme.fonts.medium, fontSize: 13, color: MUTED },

  completionBanner: {
    padding:         20,
    backgroundColor: SUCCESS_SOFT,
    borderRadius:    16,
    borderWidth:     1,
    borderColor:     'rgba(16,185,129,0.25)',
    alignItems:      'center',
    gap:             6,
  } as any,
  completionEmoji: { fontSize: 36 },
  completionTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: SUCCESS },
  completionSub:   { fontFamily: theme.fonts.regular, fontSize: 13.5, color: TEXT, textAlign: 'center' },

  // Jobs tab
  marketCards: {
    flexDirection: 'row-reverse',
    flexWrap:      'wrap',
    gap:           10,
    marginTop:     4,
  } as any,
  marketCard: {
    flex:            1,
    minWidth:        100,
    padding:         14,
    backgroundColor: 'rgba(15,23,42,0.03)',
    borderRadius:    12,
    borderWidth:     1,
    borderColor:     BORDER,
    alignItems:      'center',
    gap:             6,
  } as any,
  marketCardHL:    { backgroundColor: PRIMARY_SOFT, borderColor: 'rgba(200,83,30,0.18)' },
  marketCardNum:   { fontFamily: theme.fonts.bold, fontSize: 20, color: TEXT, textAlign: 'center' },
  marketCardLabel: { fontFamily: theme.fonts.regular, fontSize: 11.5, color: MUTED, textAlign: 'center' },

  // Salary table
  salaryTable: {
    borderRadius: 12,
    borderWidth:  1,
    borderColor:  BORDER,
    overflow:     'hidden',
    marginTop:    6,
  },
  salaryTableHeader: {
    flexDirection:   'row-reverse',
    backgroundColor: 'rgba(15,23,42,0.04)',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
  } as any,
  salaryHeaderCell: { fontFamily: theme.fonts.bold, fontSize: 12, color: MUTED, textAlign: 'center' },

  salaryRow: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
  } as any,
  salaryRowDivider: { borderBottomWidth: 1, borderBottomColor: BORDER },
  salaryLeft:  { flex: 1, gap: 2 } as any,
  salaryLabel: { fontFamily: theme.fonts.bold, fontSize: 13.5, color: TEXT, textAlign: 'right' },
  salaryExp:   { fontFamily: theme.fonts.regular, fontSize: 11, color: MUTED, textAlign: 'right' },
  salaryRight: { flex: 1.4, gap: 4 } as any,
  salaryRange: { fontFamily: theme.fonts.bold, fontSize: 13, color: PRIMARY, textAlign: 'center' },
  salaryBar:   { height: 6, backgroundColor: 'rgba(15,23,42,0.06)', borderRadius: 999 },
  salaryBarFill:{ height: '100%', backgroundColor: PRIMARY, borderRadius: 999 },

  // Salary visual
  salaryVisual: { marginTop: 4 },
  salaryVisualBar: {
    height:          14,
    backgroundColor: PRIMARY + '20',
    borderRadius:    999,
    position:        'relative',
    marginVertical:  8,
  } as any,
  salaryVisualFill: {
    position:        'absolute',
    top:             0,
    left:            0,
    right:           0,
    bottom:          0,
    backgroundColor: PRIMARY,
    borderRadius:    999,
  } as any,
  salaryVisualDot: { position: 'absolute', top: 18 } as any,
  salaryVisualLabel:{ fontFamily: theme.fonts.bold, fontSize: 11, color: PRIMARY, position: 'absolute' } as any,
  salaryVisualLegend:{ flexDirection: 'row-reverse', justifyContent: 'space-between', marginTop: 22 } as any,
  salaryVisualMin: { fontFamily: theme.fonts.medium, fontSize: 12, color: MUTED },
  salaryVisualMax: { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY },

  // Tips
  tipsBox: {
    backgroundColor: CARD,
    borderRadius:    16,
    borderWidth:     1,
    borderColor:     BORDER,
    padding:         16,
    gap:             10,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 12px rgba(15,23,42,0.05)' } as any : {}),
  } as any,
  tipsTitle: { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT, textAlign: 'right', marginBottom: 4 },
  tipItem:   { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8 } as any,
  tipDot:    { fontFamily: theme.fonts.bold, fontSize: 13, color: PRIMARY, width: 22 },
  tipText:   { flex: 1, fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT, textAlign: 'right', lineHeight: 21 },

  // CTA
  ctaSection: {
    width:           '100%',
    maxWidth:        Platform.OS === 'web' ? 780 : 720,
    marginTop:       20,
    backgroundColor: CARD,
    borderRadius:    20,
    borderWidth:     1,
    borderColor:     BORDER,
    padding:         20,
    alignItems:      'flex-end',
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 24px rgba(15,23,42,0.08)' } as any : {}),
  } as any,
  ctaTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT },
  ctaSub:   { fontFamily: theme.fonts.regular, fontSize: 13.5, color: MUTED, marginTop: 4, textAlign: 'right' },
  ctaBtns:  { flexDirection: 'row-reverse', gap: 10, marginTop: 14, flexWrap: 'wrap' } as any,
  ctaBtn: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             7,
    backgroundColor: PRIMARY,
    paddingVertical: 11,
    paddingHorizontal: 18,
    borderRadius:    12,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 14px rgba(200,83,30,0.18)' } as any : {}),
  } as any,
  ctaBtnText: { fontFamily: theme.fonts.bold, fontSize: 13.5, color: '#FAF5EA' },
  ctaBtnSecondary: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             7,
    paddingVertical: 11,
    paddingHorizontal: 18,
    borderRadius:    12,
    borderWidth:     1,
    borderColor:     'rgba(200,83,30,0.18)',
    backgroundColor: PRIMARY_SOFT,
  } as any,
  ctaBtnSecText: { fontFamily: theme.fonts.bold, fontSize: 13.5, color: PRIMARY },

  // Related
  relatedSection: {
    width:      '100%',
    maxWidth:   Platform.OS === 'web' ? 780 : 720,
    marginTop:  16,
    alignItems: 'flex-end',
  },
  relatedTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right', marginBottom: 10 },
  relatedRow:   { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10 } as any,
  relatedChip: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius:    12,
    backgroundColor: CARD,
    borderWidth:     1,
    borderColor:     BORDER,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 10px rgba(15,23,42,0.06)' } as any : {}),
  } as any,
  relatedEmoji:     { fontSize: 20 },
  relatedChipTitle: { fontFamily: theme.fonts.bold, fontSize: 13, color: TEXT },
});
